import { DEFAULT_TAX_RATE } from '../models/pricing.types';
export const pricingService = {
    calculate(input) {
        const { unitPrice, quantity, priceType, standardCapacity = 0, overflowPrice, conciergeCommissionRate, conciergeCommissionType, emantaCommissionRate, taxRate = DEFAULT_TAX_RATE, backendSubtotal, extraCharges = [], chargePercentageCompoundingEnabled = false, } = input;
        let serviceBasePrice;
        let isOverbooking = false;
        if (backendSubtotal != null) {
            serviceBasePrice = backendSubtotal;
        }
        else if (priceType === 'PERSON' &&
            overflowPrice != null &&
            quantity > standardCapacity) {
            isOverbooking = true;
            serviceBasePrice =
                standardCapacity * unitPrice +
                    (quantity - standardCapacity) * overflowPrice;
        }
        else {
            serviceBasePrice = unitPrice * quantity;
        }
        const chargeBreakdown = this.calculateExtraCharges(serviceBasePrice, extraCharges, chargePercentageCompoundingEnabled);
        const servicePrice = serviceBasePrice + chargeBreakdown.total;
        // ADR-0003: commissions and tax derive from the base service price only;
        // additional charges are payable but never part of the commission or tax base.
        let conciergeCommission;
        if (conciergeCommissionType === 'PERCENTAGE') {
            conciergeCommission = serviceBasePrice * (conciergeCommissionRate / 100);
        }
        else {
            conciergeCommission = conciergeCommissionRate * quantity;
        }
        const preTaxTotal = servicePrice + conciergeCommission;
        const vat = (serviceBasePrice + conciergeCommission) * taxRate;
        const customerTotal = preTaxTotal + vat;
        const emantaRate = emantaCommissionRate / 100;
        const emantaFromProvider = serviceBasePrice * emantaRate;
        const emantaFromReseller = conciergeCommission * emantaRate;
        const emantaRevenue = emantaFromProvider + emantaFromReseller;
        const providerPayout = servicePrice - emantaFromProvider;
        const resellerPayout = conciergeCommission - emantaFromReseller;
        return {
            serviceBasePrice: this.round(serviceBasePrice),
            servicePrice: this.round(servicePrice),
            extraCharges: chargeBreakdown.charges,
            extraChargeTotal: this.round(chargeBreakdown.total),
            conciergeCommission: this.round(conciergeCommission),
            preTaxTotal: this.round(preTaxTotal),
            vat: this.round(vat),
            customerTotal: this.round(customerTotal),
            emantaFromProvider: this.round(emantaFromProvider),
            emantaFromReseller: this.round(emantaFromReseller),
            emantaRevenue: this.round(emantaRevenue),
            providerPayout: this.round(providerPayout),
            resellerPayout: this.round(resellerPayout),
            taxRate,
            emantaCommissionRate,
            isOverbooking,
        };
    },
    aggregate(breakdowns, participantCounts = []) {
        if (breakdowns.length === 0) {
            return {
                servicePrice: 0,
                serviceBasePrice: 0,
                extraCharges: [],
                extraChargeTotal: 0,
                conciergeCommission: 0,
                preTaxTotal: 0,
                vat: 0,
                customerTotal: 0,
                emantaFromProvider: 0,
                emantaFromReseller: 0,
                emantaRevenue: 0,
                providerPayout: 0,
                resellerPayout: 0,
                taxRate: DEFAULT_TAX_RATE,
                emantaCommissionRate: 0,
                isOverbooking: false,
                totalItems: 0,
                totalParticipants: 0,
            };
        }
        let servicePrice = 0;
        let serviceBasePrice = 0;
        let extraChargeTotal = 0;
        const extraCharges = [];
        let conciergeCommission = 0;
        let vat = 0;
        let emantaFromProvider = 0;
        let emantaFromReseller = 0;
        let providerPayout = 0;
        let resellerPayout = 0;
        let isOverbooking = false;
        let totalParticipants = 0;
        for (let i = 0; i < breakdowns.length; i++) {
            const b = breakdowns[i];
            serviceBasePrice += b.serviceBasePrice;
            servicePrice += b.servicePrice;
            extraChargeTotal += b.extraChargeTotal;
            extraCharges.push(...b.extraCharges);
            conciergeCommission += b.conciergeCommission;
            vat += b.vat;
            emantaFromProvider += b.emantaFromProvider;
            emantaFromReseller += b.emantaFromReseller;
            providerPayout += b.providerPayout;
            resellerPayout += b.resellerPayout;
            if (b.isOverbooking)
                isOverbooking = true;
            totalParticipants += participantCounts[i] || 0;
        }
        const preTaxTotal = servicePrice + conciergeCommission;
        const emantaRevenue = emantaFromProvider + emantaFromReseller;
        const customerTotal = preTaxTotal + vat;
        return {
            servicePrice: this.round(servicePrice),
            serviceBasePrice: this.round(serviceBasePrice),
            extraCharges,
            extraChargeTotal: this.round(extraChargeTotal),
            conciergeCommission: this.round(conciergeCommission),
            preTaxTotal: this.round(preTaxTotal),
            vat: this.round(vat),
            customerTotal: this.round(customerTotal),
            emantaFromProvider: this.round(emantaFromProvider),
            emantaFromReseller: this.round(emantaFromReseller),
            emantaRevenue: this.round(emantaRevenue),
            providerPayout: this.round(providerPayout),
            resellerPayout: this.round(resellerPayout),
            taxRate: breakdowns[0].taxRate,
            emantaCommissionRate: breakdowns[0].emantaCommissionRate,
            isOverbooking,
            totalItems: breakdowns.length,
            totalParticipants,
        };
    },
    toCustomerView(b) {
        return {
            subtotal: b.preTaxTotal,
            vat: b.vat,
            total: b.customerTotal,
        };
    },
    toResellerView(b) {
        return {
            customerTotal: b.customerTotal,
            grossCommission: b.conciergeCommission,
            emantaDeduction: b.emantaFromReseller,
            netPayout: b.resellerPayout,
            servicePrice: b.servicePrice,
        };
    },
    toProviderView(b) {
        return {
            grossServicePrice: b.servicePrice,
            emantaDeduction: b.emantaFromProvider,
            netPayout: b.providerPayout,
            resellerCommission: b.conciergeCommission,
            customerTotal: b.customerTotal,
        };
    },
    toPlatformView(b) {
        return {
            customerTotal: b.customerTotal,
            servicePrice: b.servicePrice,
            conciergeCommission: b.conciergeCommission,
            vat: b.vat,
            emantaFromProvider: b.emantaFromProvider,
            emantaFromReseller: b.emantaFromReseller,
            emantaRevenue: b.emantaRevenue,
            providerPayout: b.providerPayout,
            resellerPayout: b.resellerPayout,
        };
    },
    round(value) {
        return Math.round(value * 100) / 100;
    },
    calculateExtraCharges(serviceBasePrice, charges, compoundPercentages) {
        let compoundableSubtotal = serviceBasePrice;
        let total = 0;
        const resolved = charges.map((charge) => {
            const compounds = charge.compoundsPercentage
                ?? charge.compounds_percentage
                ?? compoundPercentages;
            const base = compounds ? compoundableSubtotal : serviceBasePrice;
            const computedAmount = this.round(charge.type === 'PERCENTAGE' ? base * (charge.amount / 100) : charge.amount);
            if (compounds)
                compoundableSubtotal += computedAmount;
            total += computedAmount;
            return {
                ...charge,
                compoundsPercentage: compounds,
                computedAmount,
            };
        });
        return {
            charges: resolved,
            total: this.round(total),
            compoundableSubtotal: this.round(compoundableSubtotal),
        };
    },
    formatCurrency(value) {
        return `$${this.round(value).toFixed(2)}`;
    },
};
export default pricingService;
